import React, { useEffect, useState } from "react";
import FooterDisplay from "../components/footer";
import GenericModal from "../components/modals/GenericModal";
import HelpSupportButton from "../components/HelpSupportButton";
import BikerImg from "../assets/images/new_ui/biker.png";
import BuyFoodBanner from "../assets/images/new_ui/buy-reduce-de.jpg";
import { useNavigate } from "react-router-dom";
import {
  Wallet,
  Users,
  Share2,
  Copy,
  Check,
  CheckCircle,
  ArrowRight,
  ChevronLeft,
  Utensils,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import NumberFormat from "react-number-format";
import { roundDisplayPrice } from "../utils/priceHelpers";
import {
  GiftOutlined,
  StarOutlined,
  DollarOutlined,
  TrophyOutlined,
  CrownOutlined,
  FireOutlined,
} from "@ant-design/icons";
import DiscountComparisonContent from "../components/DiscountComparisonContent";

import {
  _fetch_user_wallet_data,
  _user_dashboard_referrals,
  _get_user_tier_status,
  _user_dashboard_shop,
  _get_group_accounts,
  _get_fourward_account,
} from "../common/axios_services";
import PromoBanner from "../components/PromoBanner";
import AppRoutes from "../common/routes";
import GrowRestaurantImg from "../assets/images/new_ui/grow_restaurant.jpg";
import AmbassadorImg from "../assets/images/new_ui/ambassador.png";
import useUserProfile from "../hooks/useUserProfile";
import PinActivationWatcher from "../components/PinActivationWatcher";

export default function Homepage() {
  const [weeklyOrderCount, setWeeklyOrderCount] = useState(0);
  const [globalWeeklyOrderCount, setGlobalWeeklyOrderCount] = useState(0);
  const [rateConfig, setRateConfig] = useState(null);
  const [isUserSlotEligible, setIsUserSlotEligible] = useState(true);
  const [walletData, setWalletData] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [tierStatus, setTierStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showGiveawayModal, setShowGiveawayModal] = useState(true);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const [pendingGroupInvites, setPendingGroupInvites] = useState(0);
  const [fourwardAccount, setFourwardAccount] = useState(null);

  const location = useLocation();
  const { userProfile: userData } = useUserProfile();
  const signupUserData = location.state?.signupUserData;
  const navigate = useNavigate();
  const currentUrl = "https://konnect.ws";
  const referralLink = `${currentUrl}/?konnectrfc=${userData?.accountId || ""}`;

  useEffect(() => {
    fetchUserWalletBalance();
    fetchReferralData();
    fetchTierStatus();
    fetchDashBoardShop();
    _get_group_accounts()
      .then((response) => {
        const groups = response?.data?.data || response?.data || [];
        setPendingGroupInvites(Array.isArray(groups) ? groups.filter((item) => item?.membership?.status === "PENDING").length : 0);
      })
      .catch(() => setPendingGroupInvites(0));
    _get_fourward_account()
      .then((res) => setFourwardAccount(res?.data?.data || res?.data))
      .catch(() => setFourwardAccount(null));
  }, []);

  useEffect(() => {
    const lastShown = localStorage.getItem("giveawayModalLastShown");
    const now = Date.now();
    if (!lastShown || now - parseInt(lastShown) > 10 * 60 * 1000) {
      setShowGiveawayModal(true);
      localStorage.setItem("giveawayModalLastShown", now.toString());
    }
  }, []);

  const isTier1 =
    userData?.tier === "tier_1" || tierStatus?.currentTier === "tier_1";
  const isTier2 =
    userData?.tier === "tier_2" || tierStatus?.currentTier === "tier_2";
  const isVerified = isTier1 || isTier2;
  const bonusBalance = tierStatus?.bonusBalance || 0;
  const fourwardBalance =
    walletData?.fourwardBalance ??
    (Number(fourwardAccount?.account?.depositBalance || fourwardAccount?.depositBalance || 0) +
      Number(fourwardAccount?.account?.apvBalance || fourwardAccount?.apvBalance || 0));
  const hasBonus = tierStatus?.hasBonus || false;
  const boostedBonusAmount = tierStatus?.boostedBonusAmount || 0;
  const potentialCashback = tierStatus?.potentialCashback || 0;
  const expectedSundayCashback = tierStatus?.expectedSundayCashback || 0;
  const spendTargetForDouble = tierStatus?.spendTargetForDouble || 0;

  // Weekly Start Logic (Resets every Sunday)
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? 0 : 0); // Sunday is 0
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const startOfWeek = getStartOfWeek(currentTime);
  const slotHours = rateConfig?.weekStartSlotHours || 0;
  const slotExpirationTimeMs = startOfWeek.getTime() + slotHours * 36e5;
  const msRemaining = Math.max(0, slotExpirationTimeMs - currentTime.getTime());

  const totalSlots = rateConfig?.weekStartSlotLimit || 20;
  const currentGlobalCount = globalWeeklyOrderCount || 0;
  const isSlotAvailable =
    weeklyOrderCount === 0 && totalSlots - currentGlobalCount > 0;
  const slotsRemaining = Math.max(0, totalSlots - currentGlobalCount);

  const highDiscount = rateConfig?.weekStartDiscountPercentageSlot || 70;
  const baseDiscount =
    rateConfig?.bonusDiscountWithoutBalanceSD ??
    rateConfig?.weekStartDiscountPercentageWithoutSlot ??
    12;
  const wkOrder3rdDiscountPercentage =
    rateConfig?.wkOrder3rdDiscountPercentage || 60;
  const combinedAutomaticDiscount = wkOrder3rdDiscountPercentage;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform) => {
    const text =
      "Hello You, Konnect lets you Eat More & Spend Less - 20% instant discount + Free Delivery. Click";
    const url = referralLink;

    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
      email: `mailto:?subject=${encodeURIComponent(
        "Join Konnect",
      )}&body=${encodeURIComponent(text + " " + url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        url,
      )}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url,
      )}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
  };

  const fetchUserWalletBalance = async () => {
    try {
      let balance = await _fetch_user_wallet_data();
      const data = balance.data.data;
      setWalletData(data);
    } catch (err) { }
  };

  const fetchReferralData = async () => {
    try {
      let balance = await _user_dashboard_referrals();
      const data = balance.data.data?.data || balance?.data?.data;
      setReferralData(data);
    } catch (err) { }
  };

  const fetchTierStatus = async () => {
    try {
      let res = await _get_user_tier_status();
      const data = res.data.data || res.data;
      setTierStatus(data);
    } catch (err) { }
  };

  const fetchDashBoardShop = async () => {
    try {
      const res = await _user_dashboard_shop();
      const data = res?.data?.data?.data || res?.data?.data;
      setWeeklyOrderCount(data?.weekly_orders || 0);
      setGlobalWeeklyOrderCount(data?.global_weekly_orders || 0);
      setRateConfig(data?.config || null);
      setIsUserSlotEligible(data?.isUserSlotEligible !== false);
    } catch (err) { }
  };

  const handleCloseGiveawayModal = () => {
    setShowGiveawayModal(false);
  };

  const handleGiveawayAction = () => {
    setShowGiveawayModal(false);
    navigate("/restaurant");
  };

  const handleCloseLearnMoreModal = () => {
    setShowLearnMoreModal(false);
  };

  const giveawayBody = (
    <div className="flex flex-col items-center px-1">
      <div className="text-center w-full">
        <>
          <div className="mb-4 bg-green-50 p-4 rounded-2xl inline-block border border-green-100">
            <GiftOutlined style={{ fontSize: "40px", color: "#16a34a" }} />
          </div>
          <h2 className="font-gilroyBold text-4xl text-gray-900 mb-6 leading-tight">
            Big Savings Every Time You Order!
          </h2>

          {/* <div className="mb-4">
            {isSlotAvailable && isUserSlotEligible ? (
              <div className="py-2 px-6 bg-[#16a34a] text-white rounded-lg inline-block text-lg font-gilroyBold uppercase shadow-sm">
                {highDiscount}% DISCOUNT AVAILABLE
              </div>
            ) : (
              <div className="py-2 px-6 bg-red-600 text-white rounded-lg inline-block text-lg font-gilroyBold uppercase shadow-sm">
                {highDiscount}% DISCOUNT NOT AVAILABLE
              </div>
            )}
          </div> */}

          <button
            onClick={() => {
              if (!isVerified) {
                if (!userData?.hasTransPin) {
                  setShowGiveawayModal(false);
                  navigate("/profile/trans-pin");
                } else {
                  handleGiveawayAction();
                }
              } else {
                setShowGiveawayModal(false);
                setShowLearnMoreModal(true);
              }
            }}
            className="bg-green-600 hover:bg-green-700 uppercase text-white flex flex-col items-center justify-center rounded-xl font-gilroyBold w-full transition-all shadow-lg hover:shadow-xl active:scale-95 py-4 px-6 mb-3"
          >
            <span className="text-2xl font-gilroyBold">
              {isVerified ? `HOW IT WORKS` : "ACTIVATE NOW"}
            </span>
          </button>

          {!isVerified && (
            <p
              onClick={() => {
                setShowGiveawayModal(false);
                setShowLearnMoreModal(true);
              }}
              className="text-gray-500 font-gilroyBold cursor-pointer hover:text-green-600 text-sm mb-4 uppercase tracking-wider underline"
            >
              HOW TO GET {highDiscount}% DISCOUNT WEEKLY
            </p>
          )}

          <div className="mt-8">
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 flex items-center gap-4">
              <div className="bg-orange-100 p-3 rounded-xl">
                <Utensils className="w-8 h-8 text-orange-500" />
              </div>
              <div className="text-left">
                <h3 className="font-gilroyBold text-xl text-gray-900 leading-tight">
                  Top Buyer Bonus
                </h3>
                <p className="text-gray-500 text-sm">
                  Order often for weekly cashback.
                </p>
              </div>
              <button
                onClick={handleGiveawayAction}
                className="ml-auto bg-green-600 hover:bg-green-700 text-white p-3 rounded-xl font-gilroyBold transition-all shadow-sm active:scale-95 flex items-center gap-2"
              >
                <span className="hidden sm:inline">ORDER NOW</span>
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </>
      </div>
    </div>
  );

  const renderBusinessRiderSection = () => {
    const isBiker = !!userData?.isBiker;
    const isBusiness = !!userData?.business;

    if (isBiker && isBusiness) {
      return null;
    }

    if (isBusiness) {
      return (
        <div className="mb-8">
          <div className="w-full flex flex-col md:flex-row items-center justify-between gap-6 p-8 md:p-12 border border-gray-200 rounded-2xl bg-white shadow-sm">
            <div className="flex-1 flex flex-col gap-5 md:gap-6 text-left w-full">
              <h3 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-black leading-tight tracking-tight">
                Grow Your Business With Konnect & Earn Daily.
              </h3>
              <button
                onClick={() => navigate(AppRoutes.restaurant_dashboard)}
                className="bg-black text-white py-4 px-8 text-[16px] font-bold rounded-xl self-start hover:bg-gray-800 transition"
              >
                Business dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (isBiker) {
      return (
        <div className="mb-8">
          <div className="border-2 border-gray-200 rounded-2xl bg-white shadow-sm overflow-hidden flex items-center justify-between gap-6 p-8 md:p-12 w-full">
            <div className="flex-1 flex gap-5 md:gap-6 text-left">
              <h3 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-black leading-tight tracking-tight">
                Join our delivery team and earn daily
              </h3>
              <button
                onClick={() => navigate("/biker/dashboard")}
                className="bg-black text-white py-4 px-8 text-[16px] font-bold rounded-xl self-start hover:bg-gray-800 transition"
              >
                Drivers dashboard
              </button>
            </div>
            <div className="flex-shrink-0 md:w-[420px] h-[220px] md:h-[300px]">
              <img
                src={BikerImg}
                alt="Rider"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-8 -mx-4 px-4">
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
          {/* Become a Business */}
          <div className="bg-white max-w-[320px] border-2 border-gray-100 rounded-2xl p-6 snap-start flex-shrink-0 flex flex-col justify-between gap-6 text-left shadow-sm">
            <h3 className="text-3xl font-extrabold text-black leading-tight">
              Grow Your Business With Konnect & Earn Daily.
            </h3>
            <button
              onClick={() => navigate(AppRoutes.become_business)}
              className="bg-black text-white text-[14px] font-bold py-3 px-6 self-start rounded-lg hover:bg-gray-800 transition"
            >
              Become a Business
            </button>
          </div>

          {/* Become a Rider */}
          <div className="bg-white max-w-[320px] border-2 border-gray-100 rounded-2xl p-6 snap-start flex-shrink-0 flex justify-between gap-6 text-left shadow-sm">
            <div className="flex flex-col max-w-[60%] gap-4 text-left w-full">
              <h3 className="text-3xl font-extrabold text-black leading-tight">
                Join our delivery team and earn daily
              </h3>
              <button
                onClick={() => navigate("/join-as-biker")}
                className="bg-black text-white text-[14px] font-bold py-3 px-6 self-start rounded-lg hover:bg-gray-800 transition"
              >
                Join Now
              </button>
            </div>
            <div className="w-[100px] h-[80px] flex-shrink-0">
              <img
                src={BikerImg}
                alt="Rider"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="px-4 py-6 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Welcome back, {userData?.firstName || "User"}!
          </h1>
          <p className="text-gray-600 text-xl sm:text-base">
            Here's a snapshot of your dashboard
          </p>
        </div>

        {/* Dashboard Cards - Swipeable */}
        <div className="mb-8 -mx-4 px-4">
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
            {/* Card 1: Wallet & Bonus Balance */}
            <div className="bg-white rounded-lg p-5 shadow-sm min-w-[280px] sm:min-w-[320px] snap-start flex-shrink-0 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-gray-100 p-2 rounded-lg">
                    <Wallet className="text-gray-900" size={24} />
                  </div>
                  <span
                    className={`${walletData?.percentageChange?.trend === "increasing"
                      ? "text-green-500"
                      : "text-red-500"
                      }  text-sm font-medium`}
                  >
                    {walletData?.percentageChange?.trend === "increasing"
                      ? "+"
                      : "-"}
                    ${walletData?.percentageChange?.sinceLastWeek || "0"}%
                  </span>
                </div>

                <div className="grid grid-cols-2 space-x-5">
                  <div>
                    <div className="text-gray-600 text-sm mb-1 font-gilroyBold">
                      Wallet Balance
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      <span className="small_currency">₦</span>
                      {(walletData?.availableBalance || 0).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-600 text-sm mb-1 text-right font-gilroyBold">
                      4Ward Balance
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1 text-right">
                      <span className="small_currency">₦</span>
                      {(fourwardBalance || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Referral Stats & Earn */}
            <div className="bg-white rounded-lg p-5 shadow-sm min-w-[280px] sm:min-w-[320px] snap-start flex-shrink-0 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-gray-900 p-2 rounded-lg">
                    <Users className="text-white" size={24} />
                  </div>
                </div>
                <div className="referral-stat-grid grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <div className="text-gray-600 text-sm mb-1">
                      Total Sales Income
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      ₦{roundDisplayPrice(referralData?.earnings?.totalEarned)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600 text-sm mb-1">
                      Total Referred
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {referralData?.enhanced?.total_referred || "0"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600 text-sm mb-1">
                      Verified Referrals
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {referralData?.enhanced?.total_verified || "0"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-3 flex items-center gap-2 mt-2">
                <div className="bg-yellow-200 rounded-full p-1">
                  <Users size={16} className="text-yellow-700" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Refer & Earn
                  </div>
                  <div className="text-sm text-gray-600">
                    Share your link and earn commissions
                  </div>
                </div>
              </div>
            </div>

            {/* Share Referral */}
            <div className="bg-white rounded-lg p-5 shadow-sm min-w-[280px] sm:min-w-[320px] snap-start flex-shrink-0">
              <div className="flex items-start justify-between mb-3">
                <div className="bg-blue-50 p-2 rounded-lg">
                  <Share2 className="text-blue-600" size={24} />
                </div>
              </div>
              <div className="text-gray-600 text-sm mb-3">
                Share referral link
              </div>
              <div className="flex gap-3 mb-4 justify-center">
                <button
                  onClick={() => handleShare("whatsapp")}
                  className="text-green-500 hover:bg-green-50 p-2 rounded-lg transition"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleShare("email")}
                  className="text-gray-600 hover:bg-gray-50 p-2 rounded-lg transition"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleShare("linkedin")}
                  className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleShare("facebook")}
                  className="text-blue-500 hover:bg-blue-50 p-2 rounded-lg transition"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600"
                />
                <button
                  onClick={handleCopy}
                  className="bg-black text-white px-4 py-2 rounded text-sm font-medium hover:bg-gray-800 transition flex items-center gap-1"
                >
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Promo Banner Integration */}
        <div className="contain mt-2 mb-6 text-2xl">
          <PromoBanner
            orderCount={weeklyOrderCount}
            threshold={3}
            promoCap={rateConfig?.promoCap}
            rateConfig={rateConfig}
          />
        </div>

        {/* Ambassador Card */}
        <div className="mb-8">
          <div
            onClick={() =>
              navigate(
                userData?.isAmbassador
                  ? AppRoutes.profile_referral
                  : AppRoutes.become_ambassador,
              )
            }
            className="border-2 border-[#2E6B2A] rounded-2xl p-4 sm:p-5 bg-white flex items-center gap-4 sm:gap-5 cursor-pointer hover:shadow-md transition-all duration-300"
          >
            <div className="w-[85px] h-[85px] sm:w-[110px] sm:h-[110px] flex-shrink-0 rounded-xl overflow-hidden bg-[#F4F4F4] flex items-center justify-center">
              <img
                src={AmbassadorImg}
                alt="Ambassador"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="text-right flex-1 pr-1">
              <h3 className="text-[18px] sm:text-2xl md:text-3xl font-bold text-black leading-snug tracking-tight">
                {userData?.isAmbassador
                  ? "Visit Ambassador Dashboard. Grow the pack and Earn weekly."
                  : "Become a Konnect Ambassador. Grow the pack and Earn weekly."}
              </h3>
            </div>
          </div>
        </div>

        {/* Business/Rider Section */}
        {renderBusinessRiderSection()}

        <FooterDisplay />
      </main>

      <GenericModal
        open={showGiveawayModal}
        onClose={handleCloseGiveawayModal}
        title=""
        footer={null}
      >
        {giveawayBody}
      </GenericModal>

      <DiscountComparisonContent
        open={showLearnMoreModal}
        onClose={handleCloseLearnMoreModal}
        rateConfig={rateConfig}
        isTier2={isTier2}
        navigate={navigate}
        highDiscount={highDiscount}
        baseDiscount={baseDiscount}
        footer={
          <button
            onClick={() => {
              handleCloseLearnMoreModal();
              navigate("/marketplace");
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-gilroyBold w-full transition-colors uppercase"
          >
            ORDER NOW
          </button>
        }
      />

      <HelpSupportButton page="HOME_TOOL" />
      <PinActivationWatcher />
    </div>
  );
}
